import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createVibe } from "../../api/categories";
import Loader from "../../components/Loader";
import { useTheme } from "../../context/ThemeContext";

const Step5DateTime = ({ formData, setFormData, goNext, goBack }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const [saving, setSaving] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = formData.date ? new Date(formData.date) : new Date();
    return { year: today.getFullYear(), month: today.getMonth() + 1 };
  });

  useEffect(() => {
    if (!formData.date) {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(now.getDate()).padStart(2, "0")}`;

      const dateToUse =
        formData.originalSelectedDate || location.state?.selectedDate || today;

      setFormData((prev) => ({ ...prev, date: dateToUse }));
    }
  }, [formData.date, formData.originalSelectedDate, location.state?.selectedDate, setFormData]);

  useEffect(() => {
    if (formData.date) {
      const dateObj = new Date(formData.date);
      setCalendarMonth({
        year: dateObj.getFullYear(),
        month: dateObj.getMonth() + 1,
      });
    }
  }, [formData.date]);

  const [timeSlots, setTimeSlots] = useState([]);
  useEffect(() => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        let hour12 = h % 12 === 0 ? 12 : h % 12;
        const ampm = h < 12 ? "AM" : "PM";
        slots.push(
          `${hour12.toString().padStart(2, "0")}:${m
            .toString()
            .padStart(2, "0")} ${ampm}`
        );
      }
    }
    setTimeSlots(slots);
  }, []);

  const handleTimeChange = (time) => {
    if (formData.date) {
      const selectedDate = new Date(formData.date);
      const now = new Date();
      const match = time.match(/(\d+):(\d+)\s?(AM|PM)/);
      let hour24 = 0,
        minute = 0;
      if (match) {
        hour24 = parseInt(match[1], 10);
        minute = parseInt(match[2], 10);
        const ampm = match[3];
        if (ampm === "PM" && hour24 !== 12) hour24 += 12;
        if (ampm === "AM" && hour24 === 12) hour24 = 0;
      }
      if (
        selectedDate.toISOString().split("T")[0] ===
        now.toISOString().split("T")[0]
      ) {
        if (
          hour24 < now.getHours() ||
          (hour24 === now.getHours() && minute <= now.getMinutes())
        ) {
          toast.error(t("cannot_select_previous_time"));
          return;
        }
      }
    }
    setFormData((prev) => ({ ...prev, time }));
  };

  useEffect(() => {
    if (formData.date && timeSlots.length > 0 && !formData.time) {
      const now = new Date();
      const nextSlot = timeSlots.find((slot) => {
        const match = slot.match(/(\d+):(\d+)\s?(AM|PM)/);
        if (match) {
          let hour24 = parseInt(match[1], 10);
          let minute = parseInt(match[2], 10);
          const ampm = match[3];
          if (ampm === "PM" && hour24 !== 12) hour24 += 12;
          if (ampm === "AM" && hour24 === 12) hour24 = 0;
          return (
            hour24 > now.getHours() ||
            (hour24 === now.getHours() && minute > now.getMinutes())
          );
        }
        return false;
      });
      if (nextSlot) {
        setFormData((prev) => ({ ...prev, time: nextSlot }));
      }
    }
  }, [formData.date, timeSlots]);

  const timeRefs = useRef([]);
  useEffect(() => {
    if (formData.time && timeSlots.length > 0) {
      const idx = timeSlots.findIndex((slot) => slot === formData.time);
      if (idx !== -1 && timeRefs.current[idx]) {
        timeRefs.current[idx].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [formData.time, timeSlots]);

  const handleDateChange = (day) => {
    if (!day.isOtherMonth && !day.isPrevDate) {
      setFormData((prev) => ({ ...prev, date: day.date }));
    }
  };

  const getCalendarDays = (year, month) => {
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push({ label: "", isOtherMonth: true });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = `${year}-${String(month).padStart(2, "0")}-${String(
        i
      ).padStart(2, "0")}`;
      const isPrevDate = date < todayStr;
      days.push({
        label: i,
        date,
        isOtherMonth: false,
        isToday: date === todayStr,
        isPrevDate,
      });
    }
    while (days.length % 7 !== 0) {
      days.push({ label: "", isOtherMonth: true });
    }
    return days;
  };

  return (
    <div className="w-full flex flex-col items-center min-h-screen bg-white dark:bg-black transition-colors duration-300">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 gap-4">
          <button onClick={goBack} className="py-2 rounded-lg text-gray-700 dark:text-white">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          {/* <h1 className="text-md sm:text-md md:text-lg font-bold text-[#6d2af6]">
            {t("select_time_date")}
          </h1> */}
          <button
            className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap min-w-[120px] ${
              formData.date && formData.time
                ? "bg-[#6d2af6] text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            disabled={!formData.date || !formData.time}
            onClick={async () => {
              setSaving(true);
              try {
                // Store the original local time before API call (API converts to UTC)
                const originalLocalTime = formData.time;
                
                // Ensure date and time are in formData before saving
                const payload = {
                  ...formData,
                  date: formData.date,
                  time: formData.time, // This will be converted to UTC by the API
                  steps: 5,
                };
                const response = await createVibe(payload);
                
                // Keep the original local time format (AM/PM) for display
                // Don't overwrite with UTC time from API response
                setFormData((prev) => ({
                  ...prev,
                  date: response.data?.date || prev.date,
                  // Keep the original local time, not the UTC time from API
                  time: originalLocalTime || prev.time,
                }));
                goNext();
              } catch (err) {
                toast.error(t("failed_to_save_date_time", "Failed to save date and time. Please try again."));
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? <Loader loaderColor="white" size={20} /> : t("save_next")}
          </button>
        </div>

        {/* Time Picker */}
        <div className="flex flex-row items-center justify-between gap-4 mb-8">
          <div className="flex-1 flex flex-col items-start">
            <h2 className="text-lg w-full font-bold text-gray-800 dark:text-white mb-4 text-center">
              {t("select_time")}
            </h2>
            <div className="relative w-full max-w-xs">
              <div
                className="flex flex-col items-center overflow-y-auto gap-1 rounded-2xl shadow-lg px-2 py-2 bg-gray-100 dark:bg-[#252739] transition-colors duration-300"
                style={{ maxHeight: "200px", height: "200px" }}
              >
                {timeSlots.map((slot, idx) => {
                  const isSelected = formData.time === slot;
                  return (
                    <div
                      key={idx}
                      ref={(el) => (timeRefs.current[idx] = el)}
                      onClick={() => handleTimeChange(slot)}
                      className={`text-sm w-full mx-auto py-2 text-center rounded-xl cursor-pointer text-lg font-semibold transition-all duration-150
                        ${
                          isSelected
                            ? "bg-[#6d2af6] text-white"
                            : "bg-gray-200 dark:bg-[#151729] text-gray-800 dark:text-gray-200"
                        }`}
                    >
                      {slot}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end justify-center min-w-[70px]">
            <span className="text-md font-bold text-gray-800 dark:text-white">
              {formData.time || t("no_time_selected")}
            </span>
          </div>
        </div>

        {/* Date Picker */}
        <div className="flex flex-row items-center justify-between gap-4 mb-8">
          <div className="flex-1 flex flex-col items-start">
            <h2 className="text-lg w-full text-center font-bold text-gray-800 dark:text-white mb-4">
              {t("date")}
            </h2>
            <div className="rounded-2xl w-full max-w-xs bg-white dark:bg-[#252739] p-3 transition-colors duration-300 shadow-lg">
              <div className="flex items-center justify-between mb-2 bg-[#6d2af6] rounded-lg">
                <button
                  className="text-white text-xl px-3 py-1 rounded-full"
                  onClick={() =>
                    setCalendarMonth((prev) =>
                      prev.month === 1
                        ? { year: prev.year - 1, month: 12 }
                        : { ...prev, month: prev.month - 1 }
                    )
                  }
                >
                  {"<"}
                </button>
                <span className="text-white px-4 py-2 rounded-lg font-bold text-sm">
                  {new Date(
                    calendarMonth.year,
                    calendarMonth.month - 1
                  ).toLocaleString("default", { month: "long" })}{" "}
                  {calendarMonth.year}
                </span>
                <button
                  className="text-white text-xl px-3 py-1 rounded-full"
                  onClick={() =>
                    setCalendarMonth((prev) =>
                      prev.month === 12
                        ? { year: prev.year + 1, month: 1 }
                        : { ...prev, month: prev.month + 1 }
                    )
                  }
                >
                  {">"}
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d, i) => (
                  <span key={i} className="text-sm text-gray-600 dark:text-gray-300">
                    {d}
                  </span>
                ))}
                {getCalendarDays(calendarMonth.year, calendarMonth.month).map(
                  (day, idx) => (
               <button
  key={idx}
  onClick={() => handleDateChange(day)}
  disabled={day.isOtherMonth || day.isPrevDate}
  className={`w-6 h-6 flex items-center justify-center mx-auto my-1 rounded-full text-xs sm:text-sm
    ${
      formData.date === day.date
        ? "bg-[#6d2af6] text-white font-bold shadow-md"
        : day.isPrevDate
        ? "bg-transparent text-gray-500 dark:text-gray-600 opacity-40 cursor-not-allowed"
        : day.isOtherMonth
        ? "text-gray-400 dark:text-gray-500 opacity-50"
        : "text-gray-900 dark:text-gray-200 hover:bg-[#2b2d42] hover:text-white transition-colors"
    }`}
>
  {day.label}
</button>

                  )
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end justify-center min-w-[70px]">
            <span className="text-sm sm:text-md font-bold text-gray-800 dark:text-white">
              {formData.date
                ? `${formData.date.split("-")[1]}/${formData.date.split("-")[2]}/${formData.date.split("-")[0]}`
                : t("no_date_selected")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step5DateTime;
